import useMoney from '../../../../../../../../../hooks/useMoney';
import LangEnum from '../../../../../../../../../lang/LangEnum';
import { UsedInOfflineMode } from '../../../../../../../../offline/types/UsedInOfflineMode';
import { useTranslation } from '../../../../../../../../translation/TranslationContext';
import { OrderCondition } from '../../../../../../../conditions/interfaces/orderCondition.interface';
import {
  formatOrderBillingForecastDate,
  formatOrderConditionDeliveryAddress,
  getFreightOrigin,
} from '../../../../../../../conditions/orderCondition.utils';
import { formatOrderConditionPaymentMethod } from '../../../../../../../conditions/orderConditionPaymentMethod.utils';
import { formatOrderConditionVoucher } from '../../../../../../../conditions/orderConditionVoucher.utils';
import { Order } from '../../../../../../../order.types';
import useOrderConditionFieldsVisibility from '../../../hooks/useOrderConditionFieldsVisibility';

interface UseOrderConditionDetailsReadOnlyParams extends UsedInOfflineMode {
  order: Order;
  condition: OrderCondition;
}

const useOrderConditionDetailsReadOnly = ({
  order,
  condition,
  isOfflineMode,
}: UseOrderConditionDetailsReadOnlyParams) => {
  const { formatMoney } = useMoney();
  const { translate } = useTranslation();
  const fieldsVisibility = useOrderConditionFieldsVisibility({ order, condition, isOfflineMode });

  const businessDays = condition.estimatedShippingBusinessDays;

  const commercialDiscount = condition.commercialPolicy?.discount ?? 0;

  return {
    ...fieldsVisibility,
    freightOrigin: getFreightOrigin({
      shippingCostValue: condition.shippingCostValue,
      quoteId: condition.quoteId,
      freightOrigin: condition.freightOrigin,
    }),
    freightCompanyValue: condition.shippingCompany?.commercialName,
    freightModalityValue: condition.shippingMethod ?? undefined,
    freightTermValue:
      businessDays === null || businessDays === undefined
        ? undefined
        : translate(LangEnum.ORDER_CONDITION_FREIGHT_BUSINESS_DAYS, { quantity: businessDays }),
    paymentMethodValue: formatOrderConditionPaymentMethod(condition.payment),
    billingForecastDateValue: formatOrderBillingForecastDate(condition.billingForecastDate),
    deliveryAddressValue: formatOrderConditionDeliveryAddress(condition.deliveryAddress),
    discountValue:
      commercialDiscount > 0
        ? `${commercialDiscount.toFixed(2)}% - ${condition.commercialPolicy?.name}`
        : undefined,
    voucherValue: formatOrderConditionVoucher(condition.businessPolicies.at(0), formatMoney),
    shippingCostValue:
      condition.shippingCostValue === undefined
        ? undefined
        : formatMoney(condition.shippingCostValue),
  };
};

export default useOrderConditionDetailsReadOnly;
